import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import type { DataPerformanceSummary as DataPerformanceSummaryModel } from 'webapp/services/model-manager/data-performance-summaries';

export interface Args {
	model: DataPerformanceSummaryModel;
	skipCollabsible?: boolean;
}

export default abstract class DataPerformanceSummaryBaseComponent<
	T extends Args,
> extends Component<T> {
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('account') declare accountService: Service.Account;

	feature = BillingFeature.GENERATIVE_AI;

	loadingMessages: string[] = [
		'Gathering data',
		'Analyzing data',
		'Crunching numbers',
		'Weaving your data into insights',
		'Crafting your insights',
		'Polishing the insights',
		'Assessing performance',
		'Creating data summary',
		'Creating recommendations',
		'Optimizing your recommendations',
		'Refining the details',
		'Assembling your performance summary',
		'Fine-tuning your performance summary',
		'Optimizing display',
		"It's coming together",
	];
	loadingMessageIntervalTime = (60 * 1000) / this.loadingMessages.length; // 60 seconds is summary request timeout

	generativeAIEnabled = !!this.args.model?.generativeAIEnabled;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.model.isNotTypeDynamic) {
			schedule('afterRender', () => {
				this.args.model.checkAndLoadAdditionalData();
			});
		}
	}

	get limitReached() {
		return this.mmDataPerformanceSummariesService.limitReached;
	}

	get limitReachedMessage() {
		return this.mmDataPerformanceSummariesService.limitReachedMessage;
	}

	get canReloadSummaries() {
		return !this.limitReached && !this.args.model.isSummariesDataEmpty;
	}

	get summariesLoading() {
		return this.args.model.summariesLoading;
	}

	get hideBranding(): boolean {
		return this.accountService.removeBrandingOnFeatures;
	}

	@action
	scheduleOnContentChange() {
		// extended
	}
}
